// One table describing every dashboard destination: its path, its label, the
// authorities that gate it, and whether it appears in the sidebar.
//
// The route guard, the sidebar and the post-login redirect all read from here,
// so they cannot disagree about who may go where.

import { Authority } from "../auth/authorities";

export interface NavEntry {
  /** Path under /dashboard. */
  path: string;
  label: string;
  /** Route is allowed if the user holds any one of these. Empty = ungated. */
  authorities: readonly Authority[];
  /** Whether the sidebar shows a link. */
  inSidebar: boolean;
  /** Where the post-login redirect lands for this entry. */
  landing?: string;
  children?: readonly { path: string; label: string }[];
}

const SURVEY = [
  Authority.ViewSurvey,
  Authority.EditSurvey,
  Authority.DeleteSurvey,
  Authority.CreateSurvey,
  Authority.DownloadSurvey,
  Authority.PublishSurvey,
] as const;

const NEWSFEED = [
  Authority.ViewNewsfeed,
  Authority.EditNewsfeed,
  Authority.DeleteNewsfeed,
  Authority.CreateNewsfeed,
] as const;

const USERS = [
  Authority.ViewUsers,
  Authority.CreateUser,
  Authority.EditUser,
  Authority.DeleteUser,
  Authority.ApproveUser,
  Authority.DisableUser,
] as const;

const ROLES = [
  Authority.EditRole,
  Authority.ViewRoles,
  Authority.DeleteRole,
  Authority.CreateRole,
] as const;

const REGISTRATION = [
  Authority.ViewRegistrationProperty,
  Authority.EditRegistrationProperty,
] as const;

const CALENDAR = [Authority.ViewCalendar, Authority.ApproveCalendar] as const;

export const NAV: readonly NavEntry[] = [
  {
    path: "survey",
    label: "Survey",
    authorities: SURVEY,
    inSidebar: true,
    landing: "/dashboard/survey/survey-list",
  },
  { path: "newsfeed", label: "Newsfeed", authorities: NEWSFEED, inSidebar: true },
  { path: "clergy", label: "Clergy", authorities: [Authority.ViewClergy], inSidebar: true },
  {
    path: "users",
    label: "Users",
    authorities: USERS,
    inSidebar: true,
    landing: "/dashboard/users",
  },
  {
    path: "roles",
    label: "Roles",
    authorities: ROLES,
    inSidebar: true,
    landing: "/dashboard/roles",
  },
  {
    path: "registration",
    label: "Registration",
    authorities: REGISTRATION,
    inSidebar: true,
    landing: "/dashboard/registration",
  },
  {
    // Ungated: any signed-in user can upload and delete. See README.
    path: "content-list",
    label: "Content list",
    authorities: [],
    inSidebar: true,
  },
  {
    path: "calendar-management",
    label: "Calendar approvals",
    authorities: CALENDAR,
    inSidebar: true,
    landing: "/dashboard/calendar-management",
  },
  { path: "profile", label: "Profile", authorities: [], inSidebar: false },
];

export const byPath = new Map(NAV.map((entry) => [entry.path, entry]));

/**
 * Where login sends a user. The ORDER is the behaviour, so it is spelled out
 * here rather than derived from NAV's ordering.
 */
const LANDING_ORDER = ["survey", "users", "roles", "registration", "calendar-management"] as const;

export function landingRouteFor(authorities: readonly string[]): string {
  for (const path of LANDING_ORDER) {
    const entry = byPath.get(path);
    if (!entry) continue;
    if (entry.authorities.some((a) => authorities.includes(a))) {
      return entry.landing ?? `/dashboard/${entry.path}`;
    }
  }
  return "/dashboard/unauthorized";
}
